/*
 * Copyright (c) 2022, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuthContext, Hooks } from '@asgardeo/auth-react';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import {
  tokenExchangeConfig,
  fidpAnonymous,
  asgardeoSdkConfig,
  asgardeoSdkScopes,
  fidpGoogle,
  fidpGitHub,
  fidpEnterprise,
  fidpMicrosoft,
  fidpE2EUser,
  usersManagementApiUrl,
  organizationApiUrl,
  fidpEmail,
  toSServiceName,
} from 'config';
import {
  IDP_ANONYMOUS,
  IDP_E2E,
  IDP_ENTERPRISE,
  IDP_GITHUB,
  IDP_GOOGLE,
  IDP_MICROSOFT,
} from 'data/constants';
import User from 'data/User';
import { useGetIsOrgAdmin } from 'hooks/api/Choreo/Orgs';
import { Perspectives } from 'types/perspectives';
import { ProtectedRouteLocationState } from 'types/route';
import { Organization } from 'types/userOrganization';
import { trackUserLoginError } from 'utils/tracking';
import {
  setAsgardeoISK,
  getFidpId,
  RETURN_TO_URL,
  RETURN_TO_ORG,
  VSCodeChoreoSignInConfig,
} from 'utils/auth';
import logger from 'utils/logger';
import { useSelectedOrgHandleFromUrl } from 'utils/organization';
import {
  generateBasePath,
  generatePEOverviewPath,
  generateProjectsPath,
  generateRedirectionQueryParams,
  ORG_PATH,
  PE_PATH,
  SIGN_IN_PATH,
} from 'utils/route';
import { reportError } from 'utils/sentry';
import generateUUID from 'utils/uuid';
import { useGetUserPerspectives } from './perspectives';

const useOrgTokenExchange = (
  setIsOrgAdmin: (isOrgAdmin: boolean) => void
): [(orgHandle: string) => Promise<boolean>, boolean, Error | null] => {
  const {
    state: authState,
    on,
    updateConfig,
    getDecodedIDToken,
    requestCustomGrant,
  } = useAuthContext();
  const { getIsOrgAdmin } = useGetIsOrgAdmin();
  const [isOrgTokenProgress, setIsOrgTokenProgress] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const { isAuthenticated } = authState;

  const exchangeToken = async (orgHandle: string): Promise<boolean> => {
    if (!orgHandle || !isAuthenticated) {
      return false;
    }
    setIsOrgTokenProgress(true);
    await updateConfig({
      validateIDToken: false,
    });
    try {
      const decodedIDToken = await getDecodedIDToken();
      if (!decodedIDToken.isk) {
        logger.warn(
          'ISK claim is missing in the IDToken, This would result in a failure to extend the Asgardeo session.'
        );
      } else {
        setAsgardeoISK(decodedIDToken.isk);
      }
    } catch (e) {
      logger.error('Error while getting decoded ID token', e);
    }

    const promisedTokenExchange = new Promise<boolean>((resolve, reject) => {
      // Check if registering this once is enough,or need to re-register as of now
      on(
        Hooks.CustomGrant,
        async () => {
          try {
            const isAdmin = await getIsOrgAdmin(orgHandle);
            setIsOrgAdmin(isAdmin);
            resolve(isAdmin);
          } catch (e) {
            setError(e as Error);
            logger.error(e);
            reject(e);
          } finally {
            setIsOrgTokenProgress(false);
          }
        },
        tokenExchangeConfig.id
      );
    });
    requestCustomGrant({
      ...tokenExchangeConfig,
      data: {
        ...tokenExchangeConfig.data,
        orgHandle,
      },
    });
    return promisedTokenExchange;
  };

  return [exchangeToken, isOrgTokenProgress, error];
};

const useSignInSilent = () => {
  const history = useHistory();
  const location = useLocation<ProtectedRouteLocationState>();
  const { trySignInSilently, signIn } = useAuthContext();
  const selectedOrgHandle = useSelectedOrgHandleFromUrl();
  const returnUrl = `${location.pathname}${location.search}`;

  const redirectToLogin = () => {
    trackUserLoginError(returnUrl, true);
    history.push('/login', {
      returnToUrl: returnUrl,
      returnToOrg: selectedOrgHandle || '',
    });
  };

  const redirectToLoginAsAnonymousUser = () => {
    trackUserLoginError(returnUrl, true);
    history.push(`/login?fidp=${fidpAnonymous}`, {
      returnToUrl: returnUrl,
      returnToOrg: selectedOrgHandle || '',
    });
  };

  const signInSilent = async () => {
    try {
      const tokenRequestConfig = {
        params: {
          tokenBindingId: generateUUID(),
        },
      };
      const response = await trySignInSilently(undefined, tokenRequestConfig);
      if (response === false) {
        const fidpId = getFidpId();
        if (fidpId && fidpId !== fidpAnonymous) {
          const stateParam: { [key: string]: string } = {};
          stateParam[RETURN_TO_URL] = returnUrl;
          stateParam[RETURN_TO_ORG] = selectedOrgHandle || '';
          const encodedStateParam = btoa(JSON.stringify(stateParam));

          try {
            await signIn(
              {
                fidp: fidpId,
                state: encodedStateParam,
              },
              undefined,
              undefined,
              undefined,
              undefined,
              tokenRequestConfig
            );
          } catch (signInError) {
            logger.error(signInError);
            reportError(signInError, {
              extraInfo: { signInError },
              tags: { Area: 'Auth' },
              fingerprint: ['sign-in-error'],
            });
            redirectToLogin();
          }
        } else if (fidpId && fidpId === fidpAnonymous) {
          redirectToLoginAsAnonymousUser();
        } else {
          redirectToLogin();
        }
      }
    } catch (error) {
      logger.error(error);
      reportError(error, {
        extraInfo: { error },
        tags: { Area: 'Auth' },
        fingerprint: ['login-error'],
        triggerAlert: true,
      });
      trackUserLoginError(returnUrl, true);
    }
  };

  return [signInSilent];
};

const useValidateUser = () => {
  const { httpRequest } = useAuthContext();

  const validateUser = async () => {
    const response = await httpRequest({
      url: `${usersManagementApiUrl}/validate/user?origin_cloud=${toSServiceName}`,
      method: 'GET',
    });
    const { data } = response;
    if (data?.organizations.length) {
      const { organizations, idpId, isNewUserSignup } = data;
      return {
        organizations: organizations as Organization[],
        idpId,
        isNewUserSignup,
      };
    }
    // Throw error if no organizations found
    throw new Error('No organizations found for the user');
  };
  return { validateUser };
};

const useGetOrganizations = () => {
  const { httpRequest } = useAuthContext();
  const getOrganizations = async () => {
    const response = await httpRequest({
      url: `${organizationApiUrl}/orgs`,
      method: 'GET',
    });
    const { data } = response;
    if (data?.length) {
      return data as Organization[];
    }
    throw new Error('No organizations found for the user');
  };
  return { getOrganizations };
};

const useGetDefaultOrgHandle = () => {
  const selectedOrgHandle = useSelectedOrgHandleFromUrl();
  const { state } = useAuthContext();

  const getDefaultOrgHandle = (
    organizations: Organization[],
    returnToOrg: string | undefined
  ) => {
    let organizationHandler = null;
    let orgUuid = null;

    const sessionUser = User.getUser();

    // if session stored organization is available in user's orgs list
    const sessionUserOrgMatch = organizations.find(
      (org) => org.handle === sessionUser?.organization
    );
    // If returning organization is available in user's orgs list
    const returnOrgMatch = organizations.find(
      (org) => org.handle === returnToOrg || org.handle === selectedOrgHandle
    );

    const displayNameOrgMatch = organizations.find(
      (org) => org.name.toLowerCase() === state.displayName?.toLowerCase()
    );
    if (returnOrgMatch) {
      orgUuid = returnOrgMatch.uuid;
      organizationHandler = returnOrgMatch.handle;
    }
    // If there is a returning organization or selected org from URL but that org in not available in user's orgs list
    else if (returnToOrg || selectedOrgHandle) {
      organizationHandler = null; // this is an error
    } else if (sessionUserOrgMatch) {
      orgUuid = sessionUserOrgMatch.uuid;
      organizationHandler = sessionUserOrgMatch.handle;
    } else if (displayNameOrgMatch) {
      orgUuid = displayNameOrgMatch.uuid;
      organizationHandler = displayNameOrgMatch.handle;
    } else {
      orgUuid = organizations[0].uuid;
      organizationHandler = organizations[0].handle;
    }
    if (orgUuid) {
      sessionStorage.setItem('orgUuid', orgUuid);
    }
    if (organizationHandler && !sessionUserOrgMatch) {
      User.init({ name: '', organization: organizationHandler });
    }
    return organizationHandler;
  };
  return { getDefaultOrgHandle };
};

const useNavigateToProtectedPath = () => {
  const history = useHistory();
  const location = useLocation();
  const { getUserPerspectives } = useGetUserPerspectives();
  const selectedPerspective = localStorage.getItem('selectedPerspective');

  return async (orgHandle: string, returnToUrl?: string) => {
    const userSession = User.getUser();
    const availablePerspectives = await getUserPerspectives();
    const hasOnlyPEPerspective =
      availablePerspectives.length === 1 &&
      availablePerspectives[0] === Perspectives.PE;

    const projRedirectPath = generateProjectsPath(orgHandle, '/redirect');
    const peOverviewPath = generatePEOverviewPath(orgHandle);
    const compCreateRedirectPath = generateProjectsPath(
      orgHandle,
      '/redirect-new'
    );

    // To remove query params for exact matching and to pass the query params to history.push call
    const returnToURLObject = new URL(
      `${window.location.origin}${returnToUrl || ''}` // Has to be a valid URL hence prepend location.origin
    );
    const { pathname: returnToUrlWithoutQuery, search: returnToUrlSearch } =
      returnToURLObject;

    if (returnToUrl && returnToUrlWithoutQuery === '/' && userSession) {
      // Redirect to PE perspective if user has only PE perspective or last visited perspective is PE
      if (
        hasOnlyPEPerspective ||
        (selectedPerspective === Perspectives.PE &&
          availablePerspectives.includes(Perspectives.PE))
      ) {
        history.push({
          pathname: peOverviewPath,
          search: generateRedirectionQueryParams(returnToUrlSearch),
        });
      } else {
        // If incoming has a user session and visiting the `/` path, redirect to last visited or first project
        history.push({
          pathname: projRedirectPath,
          search: generateRedirectionQueryParams(returnToUrlSearch),
        });
      }
    } else if (
      returnToUrl &&
      returnToUrlWithoutQuery === '/new' &&
      userSession
    ) {
      // If incoming has a user session and visiting the `/new` path, redirect to create component page
      history.push({
        pathname: compCreateRedirectPath,
        search: generateRedirectionQueryParams(returnToUrlSearch),
      });
    } else if (returnToUrl && returnToUrl !== '/') {
      // Else if coming to an internal page
      const isExactOrgPath = matchPath(returnToUrlWithoutQuery, {
        path: ORG_PATH,
        exact: true,
      });
      if (userSession && isExactOrgPath) {
        // If there is a user session and user is coming **exactly** to organization page (/organizations/myorg),
        // redirect the user to last visited or first project
        history.push({
          ...location,
          pathname: projRedirectPath,
          search: returnToUrlSearch,
        });
      } else {
        const match = matchPath(returnToUrl, {
          path: [ORG_PATH, PE_PATH],
        });
        // todo remove /signin exemption when /signin page content is moved to /login page
        if (match || returnToUrl === SIGN_IN_PATH) {
          history.push(returnToUrl);
        } else {
          history.replace(generateBasePath(orgHandle, returnToUrl));
        }
      }
    }
  };
};

export const getVSCodeAuthRequestURL = (configs: VSCodeChoreoSignInConfig) => {
  const {
    endpoints: { authorizationEndpoint },
    clientHost,
  } = asgardeoSdkConfig as any;
  const { captchaInfo, username, clientID, state, fidp, codeChallengeVsCode } =
    configs;

  const captchaInfoQueryParam = `&captchaInfo=${captchaInfo}`;

  return (
    `${authorizationEndpoint}?response_mode=query&prompt=login&response_type=code` +
    `&code_challenge_method=S256&code_challenge=${codeChallengeVsCode}` +
    `&fidp=${fidp}&redirect_uri=${clientHost}/vscode-auth&client_id=${clientID}` +
    `&scope=openid+${asgardeoSdkScopes.replaceAll('|', '+')}` +
    `&state=${state}${captchaInfo ? captchaInfoQueryParam : ''}${
      username ? `&username=${username}` : ''
    }`
  );
};

const useEnterpriseUser = () => {
  const { getDecodedIDToken, httpRequest } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>();

  const isEnterpriseUser = async (): Promise<boolean> => {
    const idToken = await getDecodedIDToken();
    if (idToken.is_eidp && idToken.is_eidp === 'true') {
      return true;
    }
    return false;
  };

  const registerEnterpriseUser = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await httpRequest({
        url: `${usersManagementApiUrl}/add-enterprise-user`,
        method: 'POST',
      });
      if (response.data && response.data.organizations.length > 0) {
        const organizationList: Organization[] = response.data.organizations;
        return organizationList;
      }
      return [];
    } catch (addUserError) {
      setError(addUserError);
      logger.error(addUserError);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);
  return { isEnterpriseUser, registerEnterpriseUser, isLoading, error };
};

const useFIDP = () => {
  const {
    getDecodedIDToken,
    state: { isAuthenticated },
  } = useAuthContext();
  const [fidp, setFidp] = useState('');
  const queryFidp = async () => {
    try {
      const { authenticated_idp: authenticatedIdp } = await getDecodedIDToken();
      switch (authenticatedIdp) {
        case IDP_GOOGLE:
          setFidp(fidpGoogle);
          break;
        case IDP_GITHUB:
          setFidp(fidpGitHub);
          break;
        case IDP_ANONYMOUS:
          setFidp(fidpAnonymous);
          break;
        case IDP_ENTERPRISE:
          setFidp(fidpEnterprise);
          break;
        case IDP_MICROSOFT:
          setFidp(fidpMicrosoft);
          break;
        case IDP_E2E:
          setFidp(fidpE2EUser);
          break;
        default:
          // TODO: Check the impact of this
          // queryFidp function is called only when the user is authenticated
          // If the user is authenticated but the authenticatedIdp is not found
          // then it should be the email
          setFidp(fidpEmail);
      }
    } catch (e) {
      if (!e) {
        logger.warn(
          'Can not find Authenticator ID in the auth context. Either user is not logged in ' +
            ' or you have tried to use the Asgardeo AuthContext before the initialization'
        );
      } else {
        logger.error('Error while decoding the ID token', e);
      }
    }
  };
  useEffect(() => {
    if (isAuthenticated) {
      queryFidp();
    }
  }, [isAuthenticated]);
  return fidp;
};

export {
  useOrgTokenExchange,
  useSignInSilent,
  useValidateUser,
  useGetOrganizations,
  useGetDefaultOrgHandle,
  useNavigateToProtectedPath,
  useEnterpriseUser,
  useFIDP,
};
